import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, ArrowLeft, Paperclip, Loader2, ImageIcon,
  Bug, Sparkles, HelpCircle, CreditCard, MoreHorizontal, Mail,
} from "lucide-react";

/** Type pills for new threads — must match VALID_TYPES in routes/support.py */
const THREAD_TYPES = [
  { id: "bug", label: "Bug", icon: Bug, tone: "bg-red-100 text-red-800 border-red-200" },
  { id: "feature", label: "Feature request", icon: Sparkles, tone: "bg-purple-100 text-purple-800 border-purple-200" },
  { id: "question", label: "Question", icon: HelpCircle, tone: "bg-blue-100 text-blue-800 border-blue-200" },
  { id: "billing", label: "Billing", icon: CreditCard, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { id: "other", label: "Other", icon: MoreHorizontal, tone: "bg-slate-100 text-slate-800 border-slate-200" },
];
const TYPE_BY_ID = Object.fromEntries(THREAD_TYPES.map((t) => [t.id, t]));

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 60 * 24) return `${Math.round(diffMin / 60)}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/**
 * In-app support widget — floating bubble + slide-in panel.
 * Mounted globally inside <AppShell> so it appears on every authenticated page
 * for company owners. Polls every 25s while open.
 */
export default function SupportWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // list | new | thread
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef(null);

  // Only show the widget for company users — admin uses /admin/support
  const isEligible = !!user && user.role !== "platform_admin";

  const refresh = useCallback(async () => {
    if (!isEligible) return;
    try {
      const [tList, uc] = await Promise.all([
        api.get("/support/threads"),
        api.get("/support/unread-count"),
      ]);
      setThreads(tList.data.threads || []);
      setUnreadTotal(uc.data.unread || 0);
    } catch {}
  }, [isEligible]);

  const loadThread = useCallback(async (id) => {
    if (!isEligible) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/support/threads/${id}`);
      setActiveThread(data.thread);
      setMessages(data.messages || []);
      setThreads((prev) => prev.map((t) => t.id === id ? { ...t, unread_for_company: 0 } : t));
      api.get("/support/unread-count").then((r) => setUnreadTotal(r.data.unread || 0)).catch(() => {});
    } catch {
      toast.error("Couldn't load thread");
    } finally {
      setLoading(false);
    }
  }, [isEligible]);

  // Initial fetch + polling
  useEffect(() => {
    if (!isEligible) return;
    refresh();
    const interval = setInterval(() => {
      refresh();
      if (open && view === "thread" && activeThreadId) loadThread(activeThreadId);
    }, 25_000);
    return () => clearInterval(interval);
  }, [isEligible, refresh, open, view, activeThreadId, loadThread]);

  useEffect(() => {
    if (activeThreadId && view === "thread") loadThread(activeThreadId);
  }, [activeThreadId, view, loadThread]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (view === "thread" && messagesEnd.current) {
      messagesEnd.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, view]);

  // Bail out AFTER all hooks have run
  if (!isEligible) return null;

  const openPanel = () => setOpen(true);
  const closePanel = () => { setOpen(false); setView("list"); setActiveThreadId(null); };

  return createPortal(
    <>
      {/* Floating bubble button */}
      {!open && (
        <button
          onClick={openPanel}
          aria-label="Send feedback or get support"
          data-testid="support-bubble"
          className="fixed bottom-20 lg:bottom-6 right-5 z-40 bg-[#0F172A] hover:bg-black text-white rounded-full shadow-2xl shadow-black/30 h-14 w-14 flex items-center justify-center transition-transform hover:scale-105"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadTotal > 0 && (
            <span
              data-testid="support-bubble-badge"
              className="absolute -top-1 -right-1 bg-[#C75D33] text-white text-[10px] font-extrabold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow-lg"
            >
              {unreadTotal > 9 ? "9+" : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* Slide-in panel */}
      {open && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:bottom-6 sm:right-6 sm:top-6 sm:max-w-md sm:w-[420px] flex sm:rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-slate-200 bg-white" data-testid="support-panel">
          <div className="flex-1 flex flex-col">
            {/* Panel header */}
            <div className="bg-[#0F172A] text-white px-4 py-3.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {view !== "list" && (
                  <button onClick={() => setView("list")} className="p-1 rounded hover:bg-white/10" data-testid="support-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                <MessageCircle className="w-4 h-4 shrink-0" />
                <div className="font-display font-bold text-sm truncate">
                  {view === "list" && "Support · Send feedback"}
                  {view === "new" && "New message"}
                  {view === "thread" && (activeThread?.subject || "Conversation")}
                </div>
              </div>
              <button onClick={closePanel} className="p-1.5 rounded hover:bg-white/10" data-testid="support-close-btn" aria-label="Close support panel">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Panel body */}
            {view === "list" && (
              <ThreadList
                threads={threads}
                onPick={(id) => { setActiveThreadId(id); setView("thread"); }}
                onNew={() => setView("new")}
              />
            )}
            {view === "new" && (
              <NewThreadForm
                user={user}
                onCreated={(t) => {
                  setActiveThreadId(t.id);
                  setView("thread");
                  refresh();
                }}
              />
            )}
            {view === "thread" && (
              <ThreadView
                loading={loading}
                thread={activeThread}
                messages={messages}
                user={user}
                sending={sending}
                setSending={setSending}
                onSent={() => loadThread(activeThreadId)}
                messagesEndRef={messagesEnd}
              />
            )}
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

/* ───────────────── List view ───────────────── */

function ThreadList({ threads, onPick, onNew }) {
  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-h-0">
      <div className="px-4 py-3 border-b border-slate-200 bg-white">
        <Button onClick={onNew} className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 font-bold" data-testid="support-new-thread-btn">
          <Send className="w-4 h-4 mr-1.5" /> New message
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {threads.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-500">
            <Mail className="w-7 h-7 mx-auto mb-2 text-slate-300" />
            <div className="font-semibold text-slate-700">No conversations yet</div>
            <p className="mt-1 leading-relaxed">Found a bug? Want a feature? Got a billing question? Hit "New message" and we'll get back fast.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {threads.map((t) => {
              const meta = TYPE_BY_ID[t.type] || TYPE_BY_ID.other;
              const unread = (t.unread_for_company || 0) > 0;
              return (
                <li key={t.id}>
                  <button
                    onClick={() => onPick(t.id)}
                    className="w-full text-left px-4 py-3 hover:bg-white transition-colors"
                    data-testid={`support-thread-row-${t.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>
                        <meta.icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {t.status === "closed" && (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">closed</span>
                      )}
                      {unread && <span className="ml-auto bg-[#C75D33] text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 flex items-center justify-center px-1">{t.unread_for_company}</span>}
                    </div>
                    <div className={`text-sm ${unread ? "font-extrabold text-slate-900" : "font-semibold text-slate-800"}`}>{t.subject}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{t.first_message_preview || ""}</div>
                    <div className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-widest font-bold">{fmtTime(t.last_message_at)}</div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ───────────────── New-thread form ───────────────── */

function NewThreadForm({ user, onCreated }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [type, setType] = useState("question");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Add a subject and a message");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/threads", {
        subject: subject.trim(),
        body: body.trim(),
        type,
        image_url: imageUrl,
      });
      toast.success("Sent — we'll get back to you fast");
      onCreated(data.thread);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-white">
      <div>
        <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 block">What's this about?</label>
        <div className="flex flex-wrap gap-1.5">
          {THREAD_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-lg border transition-colors ${type === t.id ? "bg-[#0F172A] text-white border-[#0F172A]" : `${t.tone} hover:opacity-80`}`}
              data-testid={`support-type-${t.id}`}
            >
              <t.icon className="w-3 h-3" /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 block">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Short summary…" className="rounded-xl" maxLength={200} data-testid="support-subject-input" />
      </div>
      <div>
        <label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 block">Message</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="What's happening? Steps to reproduce, what you expected, etc." className="rounded-xl resize-none" maxLength={8000} data-testid="support-body-input" />
      </div>
      <ImageAttach imageUrl={imageUrl} setImageUrl={setImageUrl} uploading={uploading} setUploading={setUploading} />
      <div className="pt-2">
        <Button onClick={onSubmit} disabled={submitting || uploading} className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-11 font-extrabold" data-testid="support-submit-btn">
          {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1.5" /> Send to support</>}
        </Button>
        <p className="text-[10px] text-slate-400 mt-2 text-center">We typically reply within a business day. Urgent? Email <strong>brett@windowscreenquote.com</strong>.</p>
      </div>
    </div>
  );
}

/* ───────────────── Thread view ───────────────── */

function ThreadView({ loading, thread, messages, user, sending, setSending, onSent, messagesEndRef }) {
  const [reply, setReply] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/threads/${thread.id}/messages`, { body: reply.trim(), image_url: imageUrl });
      setReply("");
      setImageUrl(null);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  if (loading || !thread) {
    return <div className="flex-1 flex items-center justify-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</div>;
  }

  const meta = TYPE_BY_ID[thread.type] || TYPE_BY_ID.other;

  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-h-0">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-white flex items-center gap-2 text-xs">
        <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>
          <meta.icon className="w-3 h-3" /> {meta.label}
        </span>
        {thread.status === "closed" && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">closed</span>
        )}
        <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-widest font-bold">{fmtTime(thread.last_message_at)}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3.5 space-y-3" data-testid="support-thread-messages">
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} isMine={m.sender_role === "company"} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t border-slate-200 bg-white p-3">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Write a reply…"
          className="rounded-xl resize-none"
          maxLength={8000}
          data-testid="support-reply-input"
        />
        <div className="flex items-center justify-between gap-2 mt-2">
          <ImageAttach imageUrl={imageUrl} setImageUrl={setImageUrl} uploading={uploading} setUploading={setUploading} compact />
          <Button onClick={send} disabled={sending || uploading || !reply.trim()} className="bg-[#0F172A] hover:bg-black rounded-xl h-9 px-4 font-bold" data-testid="support-reply-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m, isMine }) {
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 ${isMine ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-900"}`}>
        {!isMine && (
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#C75D33] mb-1">Support team</div>
        )}
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
        {m.image_url && (
          <a href={m.image_url} target="_blank" rel="noreferrer" className="block mt-2">
            <img src={m.image_url} alt="attachment" className="rounded-lg max-h-48 border border-black/5" />
          </a>
        )}
        <div className={`text-[10px] mt-1.5 ${isMine ? "text-white/55" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
      </div>
    </div>
  );
}

/* ───────────────── Image attach helper ───────────────── */

function ImageAttach({ imageUrl, setImageUrl, uploading, setUploading, compact }) {
  const fileRef = useRef(null);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/support/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.image_url);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className={compact ? "" : "rounded-xl border border-dashed border-slate-300 p-3"}>
      {imageUrl ? (
        <div className="flex items-center gap-2.5">
          <img src={imageUrl} alt="attached" className="h-10 w-10 rounded object-cover border border-slate-200" />
          <span className="text-xs font-semibold text-slate-700 flex-1 truncate">Image attached</span>
          <button onClick={() => setImageUrl(null)} className="text-slate-400 hover:text-red-600" aria-label="Remove image">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={`inline-flex items-center gap-1.5 text-xs font-bold ${compact ? "text-slate-500 hover:text-slate-900 px-1" : "text-slate-600 w-full justify-center py-1"}`}
          data-testid="support-attach-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : (compact ? "Attach image" : "Attach a screenshot (optional)")}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onChange} data-testid="support-file-input" />
    </div>
  );
}
